import React, { ChangeEvent, FC, useCallback, useState } from "react";
import StripePaymentForm from "components/StripePaymentForm";
import {
  ContentWrapper,
  Heading,
} from "containers/RightPanel/RightPanel.styles";
import { useFormik } from "formik";
import { useCurrencySymbol } from "hooks/useCurrencySymbol";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { OpenRightPanel } from "types/common/rightPanel";
import * as yup from "yup";
import { useBooksPaymentMethods } from "../../../../branding/usePaymentMethods";
import { getProfile } from "../../../../reduxFolder/reducers/auth";
import { openRightPanel } from "../../../../reduxFolder/reducers/rightPanel";
import {
  authSelectUser,
  authStripeFeePercent,
} from "../../../../reduxFolder/selectors/globalSelectors";
import api from "../../../../utils/api";
import formatter from "../../../../utils/formatter";
import { toFiniteNumber } from "../../../../utils/number";
import { calculatePaymentTotal } from "../../../../utils/paymentFees";
import { toastResponseSuccess } from "../../../../utils/responseMessageHelper";
import { finalizeStripePaymentForContext } from "../../../../utils/stripePaymentAdapter";
import {
  createAddFundsAmountValidation,
  sanitizeAddFundsAmountInput,
} from "../../helpers/addFundsAmountValidation";
import {
  BooksBalanceLayout,
  BooksBalancePanelSection,
  ContentContainer,
  FormWrapper,
} from "../../styles/BooksAddFunds.style";
import BooksAddFundsAmountFields from "../BooksAddFunds/BooksAddFundsAmountFields";
import BooksBalance from "../BooksBalance/BooksBalance";

interface BooksAddStripeFundsPanelProps {
  panelData: any;
  authStripeFeePercent: string;
  getProfile: () => Promise<void>;
  openRightPanel: OpenRightPanel;
  user: any;
}

const BooksAddStripeFundsPanel: FC<BooksAddStripeFundsPanelProps> = React.memo(
  ({ panelData, authStripeFeePercent, getProfile, openRightPanel, user }) => {
    const { t } = useTranslation("common");
    const currencySymbol = useCurrencySymbol();
    const { isAvailable } = useBooksPaymentMethods(user);
    const restoredValues = panelData.get("formValues");
    const [isCheckoutStep, setIsCheckoutStep] = useState(false);
    const { errors, values, touched, isValid, resetForm, setFieldValue } =
      useFormik<{ amount: string }>({
        initialValues: { amount: restoredValues?.amount ?? "" },
        validationSchema: yup.object({
          amount: createAddFundsAmountValidation(t, currencySymbol),
        }),
        onSubmit: () => undefined,
      });
    const amountFloat = toFiniteNumber(values.amount);
    const estimatedTotal = calculatePaymentTotal(
      amountFloat,
      authStripeFeePercent,
    );
    const helpPanelContext = {
      openRightPanel,
      returnPanelType: "BOOKS_ADD_STRIPE_FUNDS",
      getReturnPanelData: () => panelData.set("formValues", values),
    };

    const handleAmountChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        setFieldValue(
          "amount",
          sanitizeAddFundsAmountInput(event.target.value),
        );
      },
      [setFieldValue],
    );

    const createPaymentIntent = useCallback(
      () =>
        api.recharge.rechargeStripeCreatePaymentIntent({
          amount: amountFloat,
        }),
      [amountFloat],
    );

    const completePaymentIntent = useCallback(
      async (paymentIntentId: string) => {
        await finalizeStripePaymentForContext(
          { flow: "recharge" },
          paymentIntentId,
        );
        resetForm();
        await getProfile();
        toastResponseSuccess(
          t("books.stripe.successfulTransactions", {
            amount: amountFloat,
            currencySymbol,
          }),
        );
      },
      [amountFloat, currencySymbol, getProfile, resetForm, t],
    );

    const handleIntentCreated = useCallback(() => setIsCheckoutStep(true), []);
    const handleEditAmount = useCallback(() => setIsCheckoutStep(false), []);

    if (!isAvailable("stripe")) return null;

    return (
      <ContentWrapper>
        <Heading $isBordered>{t("books.addFundsPanel.chargeStripe")}</Heading>
        <ContentContainer>
          <FormWrapper>
            <BooksBalancePanelSection>
              <BooksBalanceLayout>
                <BooksBalance balance={user.get("balance")} />
              </BooksBalanceLayout>
            </BooksBalancePanelSection>
            {!isCheckoutStep && (
              <BooksAddFundsAmountFields
                amount={values.amount}
                amountError={errors.amount}
                touchedAmount={touched.amount}
                onAmountChange={handleAmountChange}
                feePercent={authStripeFeePercent}
              />
            )}
          </FormWrapper>
          <StripePaymentForm
            createPaymentIntent={createPaymentIntent}
            completePaymentIntent={completePaymentIntent}
            returnUrl={`${window.location.origin}/books/add-funds/stripe-return`}
            fallbackChildren={`${t("common.processStripe")} (${currencySymbol}${formatter.currency(estimatedTotal)})`}
            disabled={!isValid || amountFloat <= 0}
            helpPanelContext={helpPanelContext}
            onIntentCreated={handleIntentCreated}
            onEdit={handleEditAmount}
          />
        </ContentContainer>
      </ContentWrapper>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    authStripeFeePercent,
    user: authSelectUser,
  }),
  {
    getProfile,
    openRightPanel,
  },
);

export default withConnect(BooksAddStripeFundsPanel);
